import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import type { ClubTheme } from './themes';
import { useThreadMessages, useSendMessage, useMarkRead } from './queries';
import type { DirectMessage } from './queries';

interface ConversationThreadProps {
  clubId: string;
  conversationId: string;
  theme: ClubTheme;
  onBack: () => void;
  partnerName?: string;
}

export default function ConversationThread({
  clubId,
  conversationId,
  theme,
  onBack,
  partnerName,
}: ConversationThreadProps) {
  const { user } = useAuth();
  const userId = user?.id || '';

  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { data: messages = [], isLoading: loading } = useThreadMessages(clubId, conversationId);
  const sendMutation = useSendMessage(clubId, conversationId);
  const markRead = useMarkRead(clubId, conversationId);

  const scrollToEnd = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Scroll on new messages
  useEffect(() => {
    scrollToEnd();
  }, [messages.length, scrollToEnd]);

  // Mark as read on open
  useEffect(() => {
    markRead.mutate();
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sendMutation.isPending) return;
    setDraft('');
    inputRef.current?.focus();
    sendMutation.mutate(text, {
      onError: () => setDraft(text),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const grouped = groupByDate(messages);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div
        className="flex items-center gap-3 border-b px-4 py-3"
        style={{ borderColor: `${theme.colors.muted}20` }}
      >
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: theme.colors.primary }}
        >
          Back
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="truncate text-sm font-semibold" style={{ color: theme.colors.text }}>
            {partnerName || 'Conversation'}
          </h3>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
        {loading && messages.length === 0 && (
          <p className="py-8 text-center text-xs" style={{ color: theme.colors.muted }}>
            Loading messages...
          </p>
        )}

        {!loading && messages.length === 0 && (
          <p className="py-8 text-center text-xs" style={{ color: theme.colors.muted }}>
            Start the conversation.
          </p>
        )}

        {grouped.map(({ label, items }) => (
          <div key={label}>
            <div className="my-4 flex items-center gap-3">
              <div className="flex-1 border-t" style={{ borderColor: `${theme.colors.muted}20` }} />
              <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: theme.colors.muted }}>
                {label}
              </span>
              <div className="flex-1 border-t" style={{ borderColor: `${theme.colors.muted}20` }} />
            </div>
            {items.map((msg) => {
              const isMine = msg.sender_id === userId;
              return (
                <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1.5`}>
                  <div
                    className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${
                      isMine ? 'rounded-br-md' : 'rounded-bl-md'
                    }`}
                    style={{
                      backgroundColor: isMine ? theme.colors.primary : `${theme.colors.muted}15`,
                      color: isMine ? theme.colors.bg : theme.colors.text,
                    }}
                  >
                    {!isMine && (
                      <p
                        className="mb-0.5 text-[10px] font-semibold"
                        style={{ color: theme.colors.primary }}
                      >
                        {msg.sender?.full_name || 'Unknown'}
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                    <p
                      className="mt-1 text-right text-[9px]"
                      style={{ opacity: 0.6 }}
                    >
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="border-t px-4 py-3" style={{ borderColor: `${theme.colors.muted}20` }}>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 resize-none rounded-2xl px-4 py-2.5 text-sm outline-none"
            style={{
              backgroundColor: `${theme.colors.muted}10`,
              color: theme.colors.text,
              border: `1px solid ${theme.colors.muted}25`,
            }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || sendMutation.isPending}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-opacity disabled:opacity-30"
            style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2.94 5.08a1 1 0 011.2-.45L18 10l-13.86 5.37a1 1 0 01-1.37-1.2l1.5-4.17-1.5-4.17a1 1 0 01.17-.75z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function groupByDate(messages: DirectMessage[]): { label: string; items: DirectMessage[] }[] {
  const groups: { label: string; items: DirectMessage[] }[] = [];
  let currentLabel = '';

  for (const msg of messages) {
    const date = new Date(msg.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let label: string;
    if (date.toDateString() === today.toDateString()) {
      label = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      label = 'Yesterday';
    } else {
      label = date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
    }

    if (label !== currentLabel) {
      groups.push({ label, items: [msg] });
      currentLabel = label;
    } else {
      groups[groups.length - 1].items.push(msg);
    }
  }

  return groups;
}
